import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Table, Thead, Tbody, Tr, Th, Td, Button, HStack, useDisclosure,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
  ModalCloseButton, useToast, Input, Select, Badge, IconButton, Text,
  AlertDialog, AlertDialogBody, AlertDialogFooter, AlertDialogHeader,
  AlertDialogContent, AlertDialogOverlay, Spinner, Center,
} from '@chakra-ui/react';
import { FiPlus, FiEdit2, FiTrash2, FiSearch } from 'react-icons/fi';
import axiosInstance from '../../../api/AxiosInstance';
import FormularioArtista from './FormularioArtista';

const TIPOS = ['', 'Individual', 'Grupo', 'Colectivo', 'Colaborador'];

const COLORES_TIPO: Record<string, string> = {
  Individual: 'purple',
  Grupo: 'blue',
  Colectivo: 'teal',
  Colaborador: 'orange',
};

const TablaArtistasColaboradores: React.FC = () => {
  const toast = useToast();
  const [artistas, setArtistas] = useState<any[]>([]);
  const [cargando, setCargando] = useState(false);
  const [busqueda, setBusqueda] = useState('');
  const [filtroTipo, setFiltroTipo] = useState('');
  const [seleccionado, setSeleccionado] = useState<any>(null);
  const [eliminandoId, setEliminandoId] = useState<number | null>(null);
  const cancelRef = React.useRef<HTMLButtonElement>(null);

  const { isOpen: isFormOpen, onOpen: onFormOpen, onClose: onFormClose } = useDisclosure();
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const { data } = await axiosInstance.get('/cultura/artistas');
      setArtistas(data);
    } catch {
      toast({ title: 'Error al cargar artistas', status: 'error', duration: 3000 });
    } finally {
      setCargando(false);
    }
  }, [toast]);

  useEffect(() => { cargar(); }, [cargar]);

  const cargarDetalle = async (id: number) => {
    try {
      const { data } = await axiosInstance.get(`/cultura/artistas/${id}`);
      setSeleccionado(data);
    } catch {
      toast({ title: 'Error al cargar detalle', status: 'error', duration: 3000 });
    }
  };

  const handleNuevo = () => {
    setSeleccionado(null);
    onFormOpen();
  };

  const handleEditar = async (artista: any) => {
    await cargarDetalle(artista.id);
    onFormOpen();
  };

  const handleEliminarClick = (artista: any) => {
    setSeleccionado(artista);
    onDeleteOpen();
  };

  const confirmarEliminar = async () => {
    if (!seleccionado) return;
    setEliminandoId(seleccionado.id);
    try {
      await axiosInstance.delete(`/cultura/artistas/${seleccionado.id}`);
      toast({ title: 'Registro eliminado', status: 'success', duration: 3000 });
      onDeleteClose();
      cargar();
    } catch {
      toast({ title: 'Error al eliminar', status: 'error', duration: 3000 });
    } finally {
      setEliminandoId(null);
    }
  };

  const onGuardado = () => {
    onFormClose();
    cargar();
  };

  const filtrados = artistas.filter((a) => {
    const coincideBusqueda =
      a.nombre?.toLowerCase().includes(busqueda.toLowerCase()) ||
      a.disciplina?.toLowerCase().includes(busqueda.toLowerCase());
    const coincideTipo = filtroTipo ? a.tipo === filtroTipo : true;
    return coincideBusqueda && coincideTipo;
  });

  return (
    <Box>
      {/* Filtros y botón nuevo */}
      <HStack mb={4} spacing={3} wrap="wrap">
        <HStack flex={1} minW="200px">
          <FiSearch color="gray" />
          <Input
            placeholder="Buscar por nombre o disciplina..."
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            size="sm"
          />
        </HStack>
        <Select
          size="sm"
          w="160px"
          value={filtroTipo}
          onChange={(e) => setFiltroTipo(e.target.value)}
        >
          {TIPOS.map((t) => (
            <option key={t} value={t}>{t || 'Todos los tipos'}</option>
          ))}
        </Select>
        <Button
          size="sm"
          bg="#5b182b"
          color="white"
          _hover={{ bg: '#691f30' }}
          leftIcon={<FiPlus />}
          onClick={handleNuevo}
        >
          Nuevo registro
        </Button>
      </HStack>

      {cargando ? (
        <Center py={10}><Spinner color="#5b182b" /></Center>
      ) : (
        <Box overflowX="auto">
          <Table size="sm" variant="simple">
            <Thead bg="gray.50">
              <Tr>
                <Th>Tipo</Th>
                <Th>Nombre</Th>
                <Th>Disciplina</Th>
                <Th>Teléfono</Th>
                <Th>Correo</Th>
                <Th>Honorarios</Th>
                <Th>Acciones</Th>
              </Tr>
            </Thead>
            <Tbody>
              {filtrados.length === 0 ? (
                <Tr>
                  <Td colSpan={7}>
                    <Center py={6}>
                      <Text color="gray.400">No hay registros</Text>
                    </Center>
                  </Td>
                </Tr>
              ) : (
                filtrados.map((a) => (
                  <Tr key={a.id}>
                    <Td>
                      <Badge colorScheme={COLORES_TIPO[a.tipo] || 'gray'}>
                        {a.tipo}
                      </Badge>
                    </Td>
                    <Td fontWeight="medium">{a.nombre}</Td>
                    <Td>{a.disciplina || '—'}</Td>
                    <Td>{a.telefono || '—'}</Td>
                    <Td>{a.correo || '—'}</Td>
                    <Td>
                      {a.honorarios
                        ? `$${Number(a.honorarios).toLocaleString('es-MX', { minimumFractionDigits: 2 })}`
                        : '—'}
                    </Td>
                    <Td>
                      <HStack spacing={1}>
                        <IconButton
                          aria-label="Editar"
                          icon={<FiEdit2 />}
                          size="xs"
                          variant="ghost"
                          colorScheme="blue"
                          onClick={() => handleEditar(a)}
                        />
                        <IconButton
                          aria-label="Eliminar"
                          icon={<FiTrash2 />}
                          size="xs"
                          variant="ghost"
                          colorScheme="red"
                          isLoading={eliminandoId === a.id}
                          onClick={() => handleEliminarClick(a)}
                        />
                      </HStack>
                    </Td>
                  </Tr>
                ))
              )}
            </Tbody>
          </Table>
        </Box>
      )}

      {/* Modal formulario */}
      <Modal isOpen={isFormOpen} onClose={onFormClose} size="2xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader borderBottomWidth="1px" color="#5b182b">
            {seleccionado ? 'Editar registro' : 'Nuevo artista / colaborador'}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody py={4}>
            <FormularioArtista artista={seleccionado} onGuardado={onGuardado} />
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* AlertDialog eliminar */}
      <AlertDialog
        isOpen={isDeleteOpen}
        leastDestructiveRef={cancelRef}
        onClose={onDeleteClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Eliminar registro
            </AlertDialogHeader>
            <AlertDialogBody>
              ¿Eliminar a <strong>{seleccionado?.nombre}</strong>? Esta acción no se puede deshacer.
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onDeleteClose}>Cancelar</Button>
              <Button colorScheme="red" onClick={confirmarEliminar} ml={3}
                isLoading={eliminandoId !== null}>
                Eliminar
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
};

export default TablaArtistasColaboradores;
